// Generated by GitHub Copilot
import { BaseIdModel, CompanyBaseModel, CompanyTimestampedModel, Tagged, WithCustomProperties } from '#core/entities/shared/BaseModels.ts'
import { Type } from 'class-transformer'
import PartTag from '#core/entities/operations/parts/PartTag.ts'
import PartVendor from '#core/entities/operations/vendors/PartVendor.ts'
import Price, { Priced, type FormattedPrice, type PriceRates } from '#core/entities/operations/shared/Price.ts'
import { Field, type FieldMeta } from '#core/framework/decorators/field.ts'
import { initialsFromName } from '#core/utils/string-case.ts'

// Mirrors the backend's `FLOAT_TOLERANCE` (float_helpers.py): a total within this relative tolerance of the
// minimum stock counts as meeting it, so float drift is never read as a shortfall.
const LOW_STOCK_TOLERANCE = 1e-13

/** Backend `Condition` enum values (parts-specific, single-domain). */
export type Condition = 'new' | 'used' | 'repaired_in_house' | 'repaired_externally'

/** All condition enum values, in display order. */
export const CONDITION_VALUES: Condition[] = ['new', 'used', 'repaired_in_house', 'repaired_externally']

/** Maps a `Condition` enum value to its `parts:condition.*` i18n key. */
export const CONDITION_LABEL_KEYS: Record<Condition, string> = {
  new: 'condition.new',
  used: 'condition.used',
  repaired_in_house: 'condition.repaired_in_house',
  repaired_externally: 'condition.repaired_externally',
}

/** Backend `Packaging` enum values — how a vendor supplies a part (parts-specific). */
export type Packaging = 'piece' | 'box' | 'case' | 'pallet' | 'bundle' | 'other'

/** All packaging enum values, in display order. */
export const PACKAGING_VALUES: Packaging[] = ['piece', 'box', 'case', 'pallet', 'bundle', 'other']

/** Maps a `Packaging` enum value to its `parts:packaging.*` i18n key. */
export const PACKAGING_LABEL_KEYS: Record<Packaging, string> = {
  piece: 'packaging.piece',
  box: 'packaging.box',
  case: 'packaging.case',
  pallet: 'packaging.pallet',
  bundle: 'packaging.bundle',
  other: 'packaging.other',
}

/** Backend `Currency` enum values for part-vendor pricing; must mirror it exactly, since anything outside it is rejected on save. */
export const CURRENCY_VALUES = [
  'EUR', 'GBP', 'USD', 'PLN', 'SEK', 'AUD', 'BRL', 'CAD', 'CHF', 'CNY', 'CZK', 'DKK', 'HKD', 'HUF', 'IDR',
  'ILS', 'INR', 'ISK', 'JPY', 'KRW', 'MXN', 'MYR', 'NOK', 'NZD', 'PHP', 'RON', 'SGD', 'THB', 'TRY', 'ZAR',
] as const

/** A currency code a part-vendor price may be quoted in. */
export type Currency = (typeof CURRENCY_VALUES)[number]

/** Nested asset reference within a part location. */
export class PartLocationAsset {
  id: string = ''
  name: string = ''
}

/** A link between a part and an asset it is stocked at, plus its notes; `asset_id` null is the unallocated row. */
export class PartLocation extends CompanyBaseModel {
  part_id: string = ''
  asset_id: string | null = null
  notes: string | null = null

  @Type(() => PartLocationAsset)
  asset: PartLocationAsset | null = null
}

/**
 * One purchasing option for a part: what it costs and how it is packaged. `id` is
 * the price id used for PATCH/DELETE. `vendor_id` is null when the price is
 * attributed to no vendor (a supported case); vendor names come from the part's
 * {@link Part.vendors} links.
 */
export class PartVendorPrice extends CompanyTimestampedModel {
  part_id: string = ''
  vendor_id: string | null = null
  custom_id: string | null = null
  units_per_sku: number | null = null
  packaging: Packaging | null = null
  is_default: boolean = false
  /** The price of one package of `units_per_sku` units as entered, in `price_currency`. */
  price_per_order: number | null = null

  @Type(() => Price)
  price_points: Price | null = null
  /** The currency the price was entered in (lowercase). */
  price_currency: string | null = null

  /** This price as a {@link FormattedPrice} at the given rates; null when unpriced or no rate covers it. */
  formatted(rates: PriceRates): FormattedPrice | null {
    return this.price_points?.formatted(rates, this.price_currency) ?? null
  }
}

/** Nested substitute part reference. */
export class SubstitutePartRef {
  id: string = ''
  name: string = ''
  custom_id: string | null = null
}

/** A substitute part entry. */
export class PartSubstitute extends CompanyBaseModel {
  base_part_id: string = ''
  substitute_part_id: string = ''

  @Type(() => SubstitutePartRef)
  substitute_part: SubstitutePartRef | null = null
}

/** A file attached to a part; `id` is the underlying blob file id. */
export class PartFile extends BaseIdModel {
  filename: string = ''
  /** File size in bytes; null for rows that predate size tracking. */
  size: number | null = null
  content_type: string | null = null
}

/** Minimal asset row for the part's linked assets and its detail "related links" section. */
export class PartRelatedAsset extends BaseIdModel {
  name: string | null = null
}

/** Minimal work-order row for the part detail's "related links" section. */
export class PartRelatedWorkOrder extends BaseIdModel {
  sequence_id: number = 0
  title: string | null = null
}

/** Minimal work-order-template row for the part detail's "related links" section. */
export class PartRelatedTemplate extends BaseIdModel {
  title: string | null = null
}

/** Minimal periodic-maintenance row for the part detail's "related links" section. */
export class PartRelatedPeriodicMaintenance extends BaseIdModel {
  title: string | null = null
}

/** Represents a part in the operations/parts domain. */
export default class Part extends Tagged(WithCustomProperties(CompanyTimestampedModel), PartTag) {
  // `labelKey`s reuse the `parts:columns.*` keys the parts table renders, so the
  // limited-access editor labels each property exactly as its table column.
  @Field({ labelKey: 'shared.fields.name' })
  name: string = ''
  @Field({ labelKey: 'shared.fields.description' })
  description: string | null = null
  @Field({ labelKey: 'shared.fields.serialNumber' })
  serial_number: string | null = null
  @Field({ labelKey: 'shared.fields.customId' })
  custom_id: string | null = null
  @Field({ labelKey: 'shared.fields.criticality' })
  criticality: string | null = null
  @Field({ labelKey: 'columns.qrCodeData' })
  qr_code_data: string | null = null
  @Field({ labelKey: 'columns.condition' })
  condition: Condition | null = null
  @Field({ labelKey: 'columns.minimumStock' })
  minimum_stock: number | null = null

  /** Server-computed on-hand total (the unallocated quantity), on list rows and on the detail; the table's "Stock" column. */
  @Field({ labelKey: 'columns.totalQuantity', readProperties: 'locations' })
  stock_total: number = 0

  @Type(() => PartLocation)
  locations: PartLocation[] = []

  /**
   * Assets linked to this part (minimal id/name refs), on list rows and on the detail.
   * A create/update response returns it empty, so re-fetch the detail to see the links
   * a save just wrote.
   */
  @Type(() => PartLocationAsset)
  @Field({ labelKey: 'shared.fields.relatedAssets', readProperties: 'related_assets' })
  related_assets: PartLocationAsset[] = []

  /** Vendors linked to this part, each carrying its relation type(s). */
  @Type(() => PartVendor)
  @Field({ labelKey: 'columns.vendors' })
  vendors: PartVendor[] = []

  @Type(() => PartVendorPrice)
  prices: PartVendorPrice[] = []

  @Type(() => PartSubstitute)
  @Field({ labelKey: 'columns.substitutes' })
  substitutes: PartSubstitute[] = []

  @Type(() => PartFile)
  @Field({ labelKey: 'shared.fields.attachments' })
  files: PartFile[] = []

  /** Profile picture file; only the single-part GET returns it. Build its URL via `PartsApi.getPartProfilePictureUrl(id)`. */
  @Type(() => PartFile)
  profile_picture: PartFile | null = null

  /** List-row flag standing in for {@link profile_picture}, which only the single-part GET returns. */
  has_profile_picture: boolean = false

  /**
   * `true` when a minimum stock is set and the on-hand total has fallen below it.
   * Single source of truth for the low-stock highlight (list card + table). Reads
   * the server-computed {@link stock_total} — the value the list renders — so the
   * highlight and the displayed quantity can never disagree, and carries the same
   * tolerance as the backend's `low_stock` filter, so float drift is not a shortfall.
   */
  get isLowStock(): boolean {
    return this.minimum_stock != null
      && this.stock_total < this.minimum_stock - Math.abs(this.minimum_stock) * LOW_STOCK_TOLERANCE
  }

  /** Distinct assets this part is stocked at, name by id; unlocated stock contributes no entry. */
  get locationAssets(): Map<string, string> {
    const assets = new Map<string, string>()
    for (const loc of this.locations) {
      if (loc.asset) assets.set(loc.asset.id, loc.asset.name)
    }
    return assets
  }

  /** Distinct names of the assets this part is stocked at; unlocated stock contributes no name. */
  get locationNames(): string[] {
    return [...this.locationAssets.values()]
  }

  /**
   * The price of one unit from the starred price — the one a work order line freezes once it is
   * used — or null when there is no starred price, or it is unpriced or has no units per SKU.
   * Mirrors the backend's `starred_unit_prices`.
   */
  get starredUnitPrice(): Priced | null {
    const starred = this.prices.find((entry) => entry.is_default)
    const units = starred?.units_per_sku
    if (!starred?.price_points || starred.price_points.price_point < 0 || !units || units <= 0) return null
    return Object.assign(new Priced(), {
      price_points: Object.assign(new Price(), {
        price_point: starred.price_points.price_point / units,
        priced_at: starred.price_points.priced_at,
      }),
      price_currency: starred.price_currency,
    })
  }

  /** The vendor's price for the part: its default one, else its first. */
  priceFrom(vendorId: string | null): PartVendorPrice | undefined {
    const prices = this.prices.filter((price) => price.vendor_id === vendorId)
    return prices.find((price) => price.is_default) ?? prices[0]
  }

  /** Whether the vendor sells the part; without any vendors or prices on the part, that is unknown and counts as yes. */
  isSuppliedBy(vendorId: string | null): boolean {
    if (!vendorId || (this.vendors.length === 0 && this.prices.length === 0)) return true
    return this.vendors.some((vendor) => vendor.id === vendorId) || this.prices.some((price) => price.vendor_id === vendorId)
  }

  /** Every distinct vendor price as {@link PartVendorPrice.formatted} at the given rates, the default entry first; unpriced or unconvertible entries are skipped. */
  vendorPrices(rates: PriceRates): FormattedPrice[] {
    const ordered = [
      ...this.prices.filter((entry) => entry.is_default),
      ...this.prices.filter((entry) => !entry.is_default),
    ]
    const formatted = new Map<string, FormattedPrice>()
    for (const entry of ordered) {
      const price = entry.formatted(rates)
      if (price) formatted.set(`${price.display}|${price.original}`, price)
    }
    return [...formatted.values()]
  }

  /**
   * `true` when a profile picture is available, from either schema (list flag or
   * detail object). Build the URL via `PartsApi.getPartProfilePictureUrl(this.id)`.
   */
  get hasProfilePicture(): boolean {
    return this.has_profile_picture || this.profile_picture != null
  }

  /** Up to two uppercase initials derived from the part name (`'?'` when unnamed). */
  get initials(): string {
    return initialsFromName(this.name)
  }
}

/**
 * Field metadata for the parts-table columns no `@Field` on {@link Part} covers, so the
 * limited-read whitelist can resolve their `readProperties`.
 */
export const PART_VIRTUAL_FIELDS: FieldMeta[] = [
  { key: 'tags', labelKey: 'shared.fields.tags', readProperties: 'tags' },
  { key: 'location', labelKey: 'columns.locations', readProperties: 'locations' },
  { key: 'price', labelKey: 'columns.pricePerOrder', readProperties: 'prices' },
]
